import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks';
import { BaseModuleProps } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock/HeadingBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React from 'react';

import styles from './Form.module.scss';

export interface FormProps extends BaseModuleProps {
  /** HTML content to be dynamically inserted */
  htmlContent: string;
  breadcrumbs?: BreadcrumbsProps;
}

/**
 * Forms module component
 * @param props - The props for the Forms module
 * @returns The Forms module component
 */
export const Form: React.FC<FormProps> = ({
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.UNCONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  eyebrow,
  title,
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  titleType = TITLE_TYPE_OPTIONS.H2,
  bodyRichTextAsHTML,
  flowDirection = MODULE_FLOW_DIRECTION_OPTIONS.ROW,
  alignment = HEADING_ALIGNMENT_OPTIONS.START,
  ctas,
  caption,
  htmlContent,
  isFirstModule,
  breadcrumbs,
}) => {
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize,
    titleType,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
  };
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;
  const breadcrumbStyles = showBreadcrumbs ? styles.with_breadcrumb : '';

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    flowDirection,
    layout,
    moduleClassName: 'module-forms',
    isFirstModule,
    styles,
  });

  return (
    <section className={classNames(sectionStyles, breadcrumbStyles)} id={anchorTag}>
      {showBreadcrumbs && <Breadcrumbs {...breadcrumbs} className={styles.module_breadcrumbs} />}
      <div className={moduleBaseStyles}>
        <HeadingBlock className={styles['form-heading-block']} {...headingConfig} />
        <div className="mlp-webapp-form" dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>
    </section>
  );
};
